/**
 * Listing Badge utilities.
 *
 * Badges are stored in listing.attributes.publicData.badges[]
 * as an array of badge key strings, e.g. ['new', 'featured'].
 *
 * Admins can set badges via Sharetribe Integration API or manually
 * when creating/editing listings.
 */

/**
 * Extract badges from a Sharetribe listing entity.
 * @param {Object} listing — Sharetribe listing entity
 * @returns {string[]} — array of badge keys
 */
export const getBadgesFromListing = listing =>
  listing?.attributes?.publicData?.badges || [];

/**
 * Check if a listing has a specific badge.
 * @param {Object} listing
 * @param {string} badge — badge key, e.g. 'featured'
 */
export const hasBadge = (listing, badge) =>
  getBadgesFromListing(listing).includes(badge);

/**
 * Determine automatic badges based on listing data.
 * Useful for client-side badge derivation without manually setting them.
 *
 * @param {Object} listing
 * @param {Object} options
 * @param {number} options.newListingDaysThreshold — days since creation to be "new" (default: 7)
 * @returns {string[]}
 */
export const deriveAutoBadges = (listing, options = {}) => {
  const { newListingDaysThreshold = 7 } = options;
  const badges = [];

  const createdAt = listing?.attributes?.createdAt;
  if (createdAt) {
    const daysSince = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSince <= newListingDaysThreshold) badges.push('new');
  }

  const stock = listing?.currentStock?.attributes?.quantity;
  if (typeof stock === 'number' && stock === 0) badges.push('soldOut');

  return badges;
};
